import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Avatar, Badge, Spin, Divider, Button, Table } from "antd";
import { Country, State } from "country-state-city";
import {
  MailOutlined,
  PhoneOutlined,
  UserOutlined,
  TeamOutlined,
  IdcardOutlined,
  ArrowLeftOutlined,
  HomeOutlined,
  BankOutlined,
  EnvironmentOutlined,
  GlobalOutlined,
  FlagOutlined,
  SolutionOutlined,
} from "@ant-design/icons";
import { apiRequest } from "../../services/apiService";
import { ProfilePageText } from "../../constants/Constants";

const ProfilePage = () => {
  const { companyName } = useParams();
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const role = sessionStorage.getItem("Role");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await apiRequest("POST", ProfilePageText.PROFILEENDPOINT, {
          role,
          companyName,
        });

        setProfileData(data);
        setError(null);
      } catch (error) {
        console.error("Error fetching profile data:", error);

        if (error.response && error.response.status === 403) {
          setError(ProfilePageText.NOT_AUTHORIZED);
        } else {
          setError(ProfilePageText.UNABLE_TO_LOAD_PROFILE);
        }

        setProfileData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [companyName, role]);

  const getCountryName = (countryCode) => {
    if (!countryCode) return ProfilePageText.NOT_AVAILABLE;
    const country = Country.getCountryByCode(countryCode);
    return country ? country.name : countryCode;
  };

  const getStateName = (countryCode, stateCode) => {
    if (!countryCode || !stateCode) return ProfilePageText.NOT_AVAILABLE;
    const state = State.getStateByCodeAndCountry(stateCode, countryCode);
    return state ? state.name : stateCode;
  };

  const supplierColumns = [
    {
      title: 'No.',
      key: 'index',
      width: '60px',
      align: 'center',
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Supplier ID',
      dataIndex: 'supplierId',
      key: 'supplierId',
      align: 'center',
    },
    {
      title: 'Country Code',
      dataIndex: 'countryCode',
      key: 'countryCode',
      align: 'center',
    },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center p-8 bg-white rounded-lg shadow-sm max-w-md w-full">
          <h1 className="text-lg font-medium text-gray-900 mb-2">
            {error === ProfilePageText.NOT_AUTHORIZED
              ? ProfilePageText.ACCESS_DENIED
              : ProfilePageText.SOMETHING_WRONG}
          </h1>
          <p className="text-gray-500 mb-6">
            {error === ProfilePageText.NOT_AUTHORIZED
              ? ProfilePageText.NO_PERMISSION
              : ProfilePageText.UNABLE_TO_LOAD}
          </p>
          <Button
            type="primary"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
            className="hover:opacity-90"
          >
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const countryName = getCountryName(profileData.country);
  const stateName = getStateName(profileData.country, profileData.state);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-50 p-4 md:p-8">
      <div className="mx-auto max-w-7xl space-y-8">
        <Card className="hover:shadow-lg transition-all">
          <div className="flex items-center gap-4 mb-6">
            <Avatar size={64} className="bg-blue-100 text-[#014D4E] ">
              {profileData.companyName?.charAt(0)?.toUpperCase() || ""}
            </Avatar>
            <div>
              <h2 className="text-xl font-bold">
                {ProfilePageText.COMPANY_DETAILS}
              </h2>
            </div>
          </div>
          <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            <div className="flex items-start gap-3">
              <BankOutlined className="text-gray-400 text-xl mt-1" />
              <div>
                <p className="text-sm text-gray-500">
                  {ProfilePageText.COMPANY}
                </p>
                <p className="font-medium break-words">
                  {profileData.companyName || ProfilePageText.NOT_AVAILABLE}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <HomeOutlined className="text-gray-400 text-xl mt-1" />
              <div>
                <p className="text-sm text-gray-500">
                  {ProfilePageText.ADDRESS_LINE_1}
                </p>
                <p className="font-medium break-words">
                  {profileData.addressLine1 || ProfilePageText.NOT_AVAILABLE}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <HomeOutlined className="text-gray-400 text-xl mt-1" />
              <div>
                <p className="text-sm text-gray-500">
                  {ProfilePageText.ADDRESS_LINE_2}
                </p>
                <p className="font-medium break-words">
                  {profileData.addressLine2 || ProfilePageText.NOT_AVAILABLE}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <SolutionOutlined className="text-gray-400 text-xl mt-1" />
              <div>
                <p className="text-sm text-gray-500">
                  {ProfilePageText.SUPPLIER_TYPE}
                </p>
                <p className="font-medium break-words">
                  {profileData.supplierType || ProfilePageText.NOT_AVAILABLE}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <GlobalOutlined className="text-gray-400 text-xl mt-1" />
              <div>
                <p className="text-sm text-gray-500">Country</p>
                <p className="font-medium break-words">{countryName}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <FlagOutlined className="text-gray-400 text-xl mt-1" />
              <div>
                <p className="text-sm text-gray-500">State</p>
                <p className="font-medium break-words">{stateName}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <EnvironmentOutlined className="text-gray-400 text-xl mt-1" />
              <div>
                <p className="text-sm text-gray-500">
                  {ProfilePageText.ZIPCODE}
                </p>
                <p className="font-medium break-words">
                  {profileData.zipcode || ProfilePageText.NOT_AVAILABLE}
                </p>
              </div>
            </div>
          </div>
        </Card>

        <div className="grid gap-6 md:grid-cols-2">
          <Card className="hover:shadow-lg w-full transition-all">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-xl font-bold">
                  {ProfilePageText.ASSOCIATED_SUPPLIERS}
                </h1>
              </div>
            </div>
              <Table
                columns={supplierColumns}
                dataSource={profileData.associatedSuppliers}
                rowKey="supplierId"
                pagination={false}
                size="middle"
                scroll={{y:130}}
                className=" w-auto"
              />
          </Card>

          <Card className="hover:shadow-lg transition-all">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold">
                  {ProfilePageText.CONTACT_DETAILS}
                </h2>
              </div>
            </div>
            <div className="h-[180px] overflow-y-auto pr-4">
              <div className="space-y-6">
                {profileData.contactDetails?.map((contact, index) => (
                  <div key={contact.emailId}>
                    <div className="rounded-lg p-3 hover:bg-gray-100 transition-colors">
                      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2">
                        <div className="flex items-center gap-3">
                          <MailOutlined className="text-gray-400 text-xl" />
                          <div>
                            <p className="text-sm text-gray-500">
                              {ProfilePageText.EMAIL}
                            </p>
                            <p className="font-medium">
                              {contact.emailId || ProfilePageText.NOT_AVAILABLE}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <TeamOutlined className="text-gray-400 text-xl" />
                          <div>
                            <p className="text-sm text-gray-500">
                              {ProfilePageText.ROLE}
                            </p>
                            <p className="font-medium">
                              {contact.role || ProfilePageText.NOT_AVAILABLE}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <UserOutlined className="text-gray-400 text-xl" />
                          <div>
                            <p className="text-sm text-gray-500">
                              {ProfilePageText.NAME}
                            </p>
                            <p className="font-medium">
                              {contact.firstName || contact.lastName
                                ? `${contact.firstName || ""} ${
                                    contact.lastName || ""
                                  }`.trim()
                                : ProfilePageText.NOT_AVAILABLE}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <IdcardOutlined className="text-gray-400 text-xl" />
                          <div>
                            <p className="text-sm text-gray-500">
                              {ProfilePageText.DESIGNATION}
                            </p>
                            <p className="font-medium">
                              {contact.designation ||
                                ProfilePageText.NOT_AVAILABLE}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <PhoneOutlined className="text-gray-400 text-xl" />
                          <div>
                            <p className="text-sm text-gray-500">
                              {ProfilePageText.PHONE}
                            </p>
                            <p className="font-medium">
                              {contact.phoneNumber ||
                                ProfilePageText.NOT_AVAILABLE}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    {index < profileData.contactDetails.length - 1 && (
                      <Divider className="my-4" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
